import * as React from 'react';
import { useDropzone } from 'react-dropzone';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import LinearProgress from '@mui/material/LinearProgress';
import Slider from '@mui/material/Slider';
import Switch from '@mui/material/Switch';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import FileUploadOutlined from '@mui/icons-material/FileUploadOutlined';
import { alpha } from '@mui/material/styles';

import { questriOrange } from '../../../../utils/questriButtonStyles';
import {
  defaultLogoOptions,
  loadLogo,
  renderLogo,
} from './renderLogo';

const backgrounds = [
  {
    name: 'White',
    color: '#FFFFFF',
  },
  {
    name: 'Sand',
    color: '#F4F1EA',
  },
  {
    name: 'Sage',
    color: '#DDE5D9',
  },
  {
    name: 'Slate',
    color: '#344C58',
  },
  {
    name: 'Charcoal',
    color: '#202520',
  },
];

const BarnLogoEditor = ({
  onChange,
  onProcessingChange,
  disabled,
  children,
}) => {
  const [source, setSource] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [options, setOptions] = React.useState(defaultLogoOptions);
  const [expanded, setExpanded] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [isReading, setIsReading] = React.useState(false);
  const request = React.useRef(0);
  const isDisabled = disabled || isReading;

  React.useEffect(() => () => {
    request.current += 1;
  }, []);

  const applyLogo = (image, nextOptions) => {
    const result = renderLogo(image, nextOptions);
    setSource(image);
    setOptions(nextOptions);
    setPreview(result);
    setError(null);
    onChange(result);
  };

  const handleDrop = async (accepted, rejected) => {
    if (rejected.length) {
      setError('Choose one PNG, JPG, or WebP image, up to 10 MB.');
      return;
    }
    if (!accepted.length) return;
    request.current += 1;
    const currentRequest = request.current;
    setIsReading(true);
    onProcessingChange(true);
    setError(null);
    try {
      const image = await loadLogo(accepted[0]);
      if (currentRequest === request.current) {
        applyLogo(image, defaultLogoOptions);
        setExpanded(true);
      }
    } catch {
      if (currentRequest === request.current) setError('This image could not be opened. Try another PNG, JPG, or WebP file.');
    } finally {
      if (currentRequest === request.current) {
        setIsReading(false);
        onProcessingChange(false);
      }
    }
  };

  const {
    getRootProps,
    getInputProps,
    open,
    isDragActive,
  } = useDropzone({
    onDrop: handleDrop,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/webp': ['.webp'],
    },
    multiple: false,
    maxSize: 10 * 1024 * 1024,
    noClick: true,
    noKeyboard: true,
    disabled: isDisabled,
  });

  const updateOptions = (changes) => {
    try {
      applyLogo(source, {
        ...options,
        ...changes,
      });
    } catch {
      setError('That adjustment could not be applied. Your previous logo is still selected.');
    }
  };

  const removeLogo = () => {
    setSource(null);
    setPreview(null);
    setOptions(defaultLogoOptions);
    setExpanded(false);
    setError(null);
    onChange(null);
  };

  const borderColor = isDragActive
    ? questriOrange.main
    : 'divider';

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          sm: 'minmax(0, 1fr) 240px',
          md: 'minmax(0, 1fr) 320px',
        },
        alignItems: 'center',
        gap: 2,
        mt: 2,
      }}
    >
      {children}
      <Box>
        <Box
          {...getRootProps()}
          sx={{
            border: '1px dashed',
            borderColor,
            borderRadius: 2,
            p: 2,
            textAlign: 'center',
            backgroundColor: alpha(questriOrange.main, 0.03),
          }}
        >
          <input {...getInputProps({ 'aria-label': 'Upload Barn logo' })} />
          {!preview && (
            <>
              <FileUploadOutlined
                sx={{
                  color: 'text.secondary',
                  fontSize: 30,
                }}
              />
              <Typography variant="body2" sx={{ mb: 1 }}>Your Barn logo</Typography>
              <Button onClick={open} disabled={isDisabled} variant="outlined">Upload a logo</Button>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                Or drop a PNG, JPG, or WebP here. Up to 10 MB.
              </Typography>
            </>
          )}
          {preview && (
            <>
              <Box
                component="img"
                src={preview}
                alt="Your customized Barn logo"
                sx={{
                  width: 128,
                  height: 128,
                  borderRadius: '50%',
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'block',
                  mx: 'auto',
                  mb: 1,
                }}
              />
              <Button onClick={open} disabled={isDisabled}>Replace</Button>
              <Button onClick={removeLogo} disabled={isDisabled}>Remove</Button>
            </>
          )}
          {isReading && <LinearProgress aria-label="Preparing your logo" sx={{ mt: 1 }} />}
        </Box>
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
          Optional. You can also add a logo later in Barn settings.
        </Typography>
      </Box>
      {preview && (
        <Accordion
          expanded={expanded}
          onChange={(event, nextExpanded) => setExpanded(nextExpanded)}
          disableGutters
          elevation={0}
          sx={{
            gridColumn: '1 / -1',
            backgroundColor: 'transparent',
            '&::before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreRounded />}
            id="logo-options-heading"
            aria-controls="logo-options-content"
            sx={{ backgroundColor: alpha(questriOrange.main, 0.08) }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                textTransform: 'none',
              }}
            >
              Adjust your logo (optional)
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            id="logo-options-content"
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                md: 'repeat(3, minmax(0, 1fr))',
              },
              gap: 3,
            }}
          >
            <Box>
              <Typography id="logo-size-label" variant="body2">Logo size</Typography>
              <Slider
                aria-labelledby="logo-size-label"
                aria-valuetext={`${options.zoom}%`}
                value={options.zoom}
                onChange={(event, value) => updateOptions({ zoom: value })}
                disabled={isDisabled}
                min={50}
                max={300}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value}%`}
                sx={{
                  width: 'calc(100% - 16px)',
                  mx: 1,
                }}
              />
              <Typography variant="caption" display="block" color="text.secondary">
                Go smaller for more space, or zoom in to crop existing padding. Check the preview for cropped edges.
              </Typography>
              <Button onClick={() => updateOptions({ zoom: defaultLogoOptions.zoom })} disabled={isDisabled}>
                Fit whole logo
              </Button>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Background</Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                {backgrounds.map((background) => (
                  <Button
                    key={background.color}
                    aria-label={`${background.name} background`}
                    aria-pressed={options.background === background.color}
                    disabled={isDisabled}
                    onClick={() => updateOptions({ background: background.color })}
                    sx={{
                      minWidth: 36,
                      minHeight: 44,
                      p: 0,
                      border: '2px solid',
                      borderColor: options.background === background.color
                        ? questriOrange.main
                        : 'divider',
                      backgroundColor: background.color,
                      '&:hover': { backgroundColor: background.color },
                    }}
                  />
                ))}
              </Box>
              <Box
                component="label"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  my: 1,
                }}
              >
                <Box
                  component="input"
                  type="color"
                  aria-label="Custom background color"
                  value={options.background}
                  disabled={isDisabled}
                  onChange={(event) => updateOptions({ background: event.target.value })}
                  sx={{
                    width: 44,
                    height: 44,
                    p: 0,
                    border: 0,
                    cursor: 'pointer',
                  }}
                />
                <Typography variant="body2">Choose your own color</Typography>
              </Box>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 2 }}>
                Transparent logos work best with colored backgrounds.
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Logo finish</Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                aria-label="Logo finish"
                value={options.filter}
                disabled={isDisabled}
                onChange={(event, value) => value && updateOptions({ filter: value })}
              >
                <ToggleButton value="original">Original</ToggleButton>
                <ToggleButton value="grayscale">B&amp;W</ToggleButton>
                <ToggleButton value="warm">Warm</ToggleButton>
              </ToggleButtonGroup>
              <FormControlLabel
                control={(
                  <Switch
                    checked={options.wordmark}
                    disabled={isDisabled}
                    onChange={(event) => updateOptions({ wordmark: event.target.checked })}
                  />
                )}
                label={<Typography variant="body2">Add a Questri label</Typography>}
                sx={{ mt: 1 }}
              />
              <Button onClick={() => updateOptions(defaultLogoOptions)} disabled={isDisabled}>Reset styling</Button>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

export default BarnLogoEditor;
